// World lifecycle (§18.7), hashing (§18.4), touch credit (§2.11) and the shared world queries of
// §29.3. Bean helpers and spawning live in bean.ts / spawn.ts and are re-exported here.
import { INTRO_TICKS, MAP_TILE_COUNT, MAP_WEDGE_COUNT, MAX_BEANS, NO_SLOT, TUNE } from '../constants';
import { BS, ItemKind, MapId, ModeId, isHazardCause, type Cause } from '../ids';
import { roundClock } from '../maps/clock';
import { MAPS } from '../maps/index';
import { MODES } from '../modes/index';
import { seedRng, shuffle } from '../rng';
import type { Bean, Entity, MapState, ModeState, RoundConfig, World } from '../types';
import { createBean } from './bean';
import { itemCharges } from './items';
import { createLagHistory } from './lagcomp';
import { buildRules } from './rules';
import { spawnBean } from './spawn';

export { createBean, deriveBeanStats, hasBody, resetBean } from './bean';
export { pickSpawn, respawnBean, spawnBean } from './spawn';

export function createMapState(): MapState {
  return {
    tiltX: 0, tiltY: 0, wedgeFallen: 0, wedgeWarn: 0,
    wedgeOrder: new Array<number>(MAP_WEDGE_COUNT).fill(0),
    tileState: new Array<number>(MAP_TILE_COUNT).fill(0),
    tileTimer: new Array<number>(MAP_TILE_COUNT).fill(0),
    sdCursor: 0, cupOccupant: NO_SLOT,
  };
}

export function createModeState(): ModeState {
  return {
    potatoSlot: NO_SLOT, potatoFuse: 0, potatoFuseMax: 0, potatoNextTick: 0,
    potatoPasser: NO_SLOT, potatoPassTick: 0, potatoIntensity: 0,
    crownEntId: 0, crownHolder: NO_SLOT, crownRespawnTick: 0,
    matchPoint: false, aliveAtStart: 0,
  };
}

/** Royale, Hot Potato and Crown Grab score; the Lobby and the Podium do not (no intro, no crates). */
export function isScoringMode(modeId: ModeId): boolean {
  return modeId !== ModeId.LOBBY && modeId !== ModeId.PODIUM;
}

/** True while `stepWorld` runs in predict mode (§18.2). Server-authoritative effects check this. */
export function isPredicting(w: World): boolean {
  return w.predictSlot !== undefined && w.predictSlot !== NO_SLOT;
}

/** In predict mode every bean except the local one is a puppet: no inputs, timers or actions. */
export function isPuppet(w: World, slot: number): boolean {
  return isPredicting(w) && slot !== w.predictSlot;
}

/** Both slots are beans on the same team in a Teams round (§4.5). */
export function isTeammate(w: World, a: number, b: number): boolean {
  if (!w.cfg.teamsOn || a === NO_SLOT || b === NO_SLOT || a === b) return false;
  const team = w.beans[a].team;
  return team !== 0 && team === w.beans[b].team;
}

/**
 * Podium pedestals (§6.6): the Podium's first three spawns. A literal rather than an import of
 * `maps/podium` so the sim never loads a map file directly (only the getter registry): map modules
 * import sim modules, and a direct import would evaluate a map's top level in the middle of that
 * cycle.
 */
const PODIUM_PEDESTALS = 3;

/**
 * Spawn index per slot. Slots get a seeded shuffle of the map's spawns; on the Podium the top
 * three match scores (ties by slot) take the pedestals in place order and the rest are shuffled
 * over the remaining spawns.
 */
function spawnOrder(w: World): number[] {
  const spawnCount = MAPS[w.cfg.mapId].spawns.length;
  const order = new Array<number>(MAX_BEANS).fill(0);
  if (w.cfg.mapId !== MapId.PODIUM) {
    const idx = shuffle(w, Array.from({ length: spawnCount }, (_, i) => i));
    for (let slot = 0; slot < MAX_BEANS; slot++) order[slot] = idx[slot % spawnCount];
    return order;
  }
  const score = (slot: number): number => w.cfg.matchScores[slot] ?? 0;
  const ranked = w.beans.filter((b) => b.active).map((b) => b.slot).sort((a, b) => score(b) - score(a) || a - b);
  const rest = shuffle(w, Array.from({ length: spawnCount - PODIUM_PEDESTALS }, (_, i) => PODIUM_PEDESTALS + i));
  ranked.forEach((slot, rank) => {
    order[slot] = rank < PODIUM_PEDESTALS ? rank : rest[(rank - PODIUM_PEDESTALS) % rest.length];
  });
  return order;
}

/**
 * New round world (§18.7). Scoring modes start at tick −INTRO_TICKS − 1 with active beans FROZEN,
 * so the first step yields −180 and COUNTDOWN 3; Lobby and Podium start at −1. Beans are placed on
 * the map's spawns (consuming world RNG for the shuffle), then the map and mode initialize their
 * state, and Golden start items are granted. `cfg` and `rules` are immutable for the round.
 */
export function createWorld(cfg: RoundConfig, roundId: number, withHistory: boolean): World {
  const scoring = isScoringMode(cfg.modeId);
  const tick = scoring ? -INTRO_TICKS - 1 : -1;
  const w: World = {
    tick, roundId, cfg,
    rules: buildRules(cfg),
    rng: seedRng(cfg.seed),
    phase: roundClock(cfg.mapId, cfg.modeId, tick, cfg.capTickOverride).phase,
    beans: [], ents: [], nextEntId: 1,
    map: createMapState(), mode: createModeState(),
    crateNextTick: TUNE.crate.firstTick,
    over: false, winner: NO_SLOT, koOrder: [], events: [],
    hist: withHistory ? createLagHistory() : null,
  };
  for (let slot = 0; slot < MAX_BEANS; slot++) {
    const b = createBean(slot);
    b.active = cfg.activeSlots[slot] === true;
    b.team = cfg.teams[slot] ?? 0;
    w.beans.push(b);
  }

  const map = MAPS[cfg.mapId];
  const order = spawnOrder(w);
  for (const b of w.beans) {
    if (!b.active) continue;
    const p = map.spawns[order[b.slot]];
    spawnBean(w, b.slot, p.x, p.y);
    if (scoring) b.state = BS.FROZEN;
    const item = cfg.startItems[b.slot] ?? ItemKind.NONE;
    if (item !== ItemKind.NONE) {
      b.item = item;
      b.charges = itemCharges(item);
    }
  }
  map.initState(w);
  MODES[cfg.modeId].init(w);
  return w;
}

function cloneBean(b: Bean): Bean {
  return {
    ...b,
    pos: { x: b.pos.x, y: b.pos.y }, vel: { x: b.vel.x, y: b.vel.y },
    lungeDir: { x: b.lungeDir.x, y: b.lungeDir.y },
    edgePoint: { x: b.edgePoint.x, y: b.edgePoint.y }, edgeNormal: { x: b.edgeNormal.x, y: b.edgeNormal.y },
    prev: { ...b.prev },
  };
}

function cloneEntity(e: Entity): Entity {
  return { ...e, pos: { x: e.pos.x, y: e.pos.y }, vel: { x: e.vel.x, y: e.vel.y } };
}

/**
 * Deep copy for prediction, without lag history or pending events. The round's `cfg` and `rules`
 * are shared by reference: they never change during a round. The copy records the tick it was
 * taken at (`predictBaseTick`), which bounds puppet dead-reckoning in predict mode.
 */
export function cloneWorld(w: World): World {
  return {
    ...w,
    beans: w.beans.map(cloneBean),
    ents: w.ents.map(cloneEntity),
    map: { ...w.map, wedgeOrder: w.map.wedgeOrder.slice(), tileState: w.map.tileState.slice(), tileTimer: w.map.tileTimer.slice() },
    mode: { ...w.mode },
    koOrder: w.koOrder.slice(),
    events: [],
    hist: null,
    predictSlot: undefined,
    predictBaseTick: w.tick,
  };
}

const FNV_OFFSET = 2166136261;
const FNV_PRIME = 16777619;
const HASH_QUANT = 1e4;

function mixInt(h: number, v: number): number {
  const x = v | 0;
  h = Math.imul(h ^ (x & 0xff), FNV_PRIME);
  h = Math.imul(h ^ ((x >>> 8) & 0xff), FNV_PRIME);
  h = Math.imul(h ^ ((x >>> 16) & 0xff), FNV_PRIME);
  return Math.imul(h ^ (x >>> 24), FNV_PRIME);
}

function mixFloat(h: number, v: number): number {
  return mixInt(h, Math.round(v * HASH_QUANT));
}

/**
 * FNV-1a over beans (state, st, pos, vel, z, vz, dizzy, item, charges, out), entities and map
 * state, with floats quantized to 1e-4 (§18.4). Used by the determinism and Node/Chrome tests.
 */
export function hashWorld(w: World): number {
  let h = FNV_OFFSET;
  for (const b of w.beans) {
    h = mixInt(h, b.state);
    h = mixInt(h, b.st);
    h = mixFloat(h, b.pos.x);
    h = mixFloat(h, b.pos.y);
    h = mixFloat(h, b.vel.x);
    h = mixFloat(h, b.vel.y);
    h = mixFloat(h, b.z);
    h = mixFloat(h, b.vz);
    h = mixFloat(h, b.dizzy);
    h = mixInt(h, b.item);
    h = mixInt(h, b.charges);
    h = mixInt(h, b.out ? 1 : 0);
  }
  h = mixInt(h, w.ents.length);
  for (const e of w.ents) {
    h = mixInt(h, e.id);
    h = mixInt(h, e.kind);
    h = mixInt(h, e.owner);
    h = mixFloat(h, e.pos.x);
    h = mixFloat(h, e.pos.y);
    h = mixFloat(h, e.vel.x);
    h = mixFloat(h, e.vel.y);
    h = mixFloat(h, e.z);
    h = mixFloat(h, e.vz);
    h = mixInt(h, e.t);
    h = mixInt(h, e.timer);
    h = mixInt(h, e.data);
    h = mixInt(h, e.data2);
  }
  const m = w.map;
  h = mixFloat(h, m.tiltX);
  h = mixFloat(h, m.tiltY);
  h = mixInt(h, m.wedgeFallen);
  h = mixInt(h, m.wedgeWarn);
  for (const v of m.wedgeOrder) h = mixInt(h, v);
  for (const v of m.tileState) h = mixInt(h, v);
  for (const v of m.tileTimer) h = mixInt(h, v);
  h = mixInt(h, m.sdCursor);
  h = mixInt(h, m.cupOccupant);
  return h >>> 0;
}

/**
 * Records who last touched `victim` (§2.11). Hazard causes never overwrite an existing credited
 * touch: they only record the latest impulse cause and refresh the credit's tick while it is
 * still inside the credit window.
 */
export function setTouch(w: World, victim: number, by: number, cause: Cause, aux = 0): void {
  const b = w.beans[victim];
  if (isHazardCause(cause)) {
    refreshTouch(w, b, cause);
    return;
  }
  b.touchBy = by;
  b.touchTick = w.tick;
  b.touchCause = cause;
  b.touchAux = aux;
}

/**
 * An impulse that claims no credit (§2.11: hazards, self-inflicted hits, impulses without an
 * attacker): records it as the latest impulse cause and refreshes an existing credit's tick while
 * it is still inside the credit window. Never sets `touchBy`.
 */
export function refreshTouch(w: World, b: Bean, cause: Cause): void {
  b.touchCause = cause;
  if (b.touchBy !== NO_SLOT && w.tick - b.touchTick <= TUNE.credit.windowTicks) b.touchTick = w.tick;
}

/** The slot credited for a KO of `b` right now (§2.11), or NO_SLOT for a self-fall. */
export function creditOf(w: World, b: Bean): number {
  return b.touchBy !== NO_SLOT && w.tick - b.touchTick <= TUNE.credit.windowTicks ? b.touchBy : NO_SLOT;
}

/** Beans still in play: active, not out and not FALLING. */
export function aliveCount(w: World): number {
  let n = 0;
  for (const b of w.beans) if (b.active && !b.out && b.state !== BS.FALLING) n++;
  return n;
}
